/**
 * @module-isolated analytics-reuniones
 * ROLLBACK_POINT: reuniones-fase-a-2026-08-04 (UI presentación)
 * Página: lectura → KPIs por cliente → embudo → ritmo → ranking por contacto → detalle.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AlertList,
  ErrorState,
  ModuleFilters,
  PageHeader,
  SectionHeader,
  SimplePieChart,
  SimpleTable,
  SourceBanner
} from '../../../shared/components/index.js';
import { useModuleFilters } from '../../../shared/hooks/useModuleFilters.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';
import { formatAnalyticsNumber } from '../../../shared/utils/format-number.js';
import { fetchMeetingsDashboard } from '../services/meetingsService.js';
import {
  aggregateByWeek,
  buildMeetingsReading,
  clientRankBars,
  kpiMap,
  kpiValue
} from '../utils/meetings-present.js';

const PRIMARY_KPIS = [
  'Agendadas',
  'Con contacto Kommo',
  'Con cliente (sin Kommo)',
  'Sin cliente',
  'Contactos Kommo distintos',
  'Clientes distintos'
];

export default function ReunionesPage() {
  const { email } = useOutletContext() || {};
  const filters = useModuleFilters({ persistKey: 'mod:reuniones' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [tick, setTick] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const forceRef = useRef(false);
  const loadSeq = useRef(0);

  useAnalyticsRefresh(
    useCallback(() => {
      if (!hasSearched) return;
      forceRef.current = true;
      setTick((t) => t + 1);
    }, [hasSearched])
  );

  const loadData = useCallback((f) => fetchMeetingsDashboard(f), []);

  useEffect(() => {
    if (!hasSearched) return;
    let cancelled = false;
    const seq = ++loadSeq.current;
    const force = forceRef.current;
    forceRef.current = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await loadData({
          period: filters.period,
          dateFrom: filters.range.dateFrom,
          dateTo: filters.range.dateTo,
          status: filters.status,
          force
        });
        if (cancelled || seq !== loadSeq.current) return;
        setData(payload);
      } catch (err) {
        if (cancelled || seq !== loadSeq.current) return;
        setError((err && err.message) || 'Error al cargar reuniones.');
        if (!data) setData(null);
      } finally {
        if (!cancelled && seq === loadSeq.current) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  function runSearch() {
    setHasSearched(true);
    forceRef.current = true;
    setTick((t) => t + 1);
  }

  function cancelSearch() {
    loadSeq.current += 1;
    setLoading(false);
  }

  const catalogs = (data && data.catalogs) || {};
  const kpis = useMemo(() => kpiMap((data && data.kpis) || []), [data]);
  const reading = useMemo(() => buildMeetingsReading((data && data.kpis) || []), [data]);
  const weeks = useMemo(
    () => aggregateByWeek((data && data.charts && data.charts.porFecha) || []),
    [data]
  );
  const clientRank = useMemo(() => {
    const prog = Number(kpiValue(kpis, 'Agendadas')) || 0;
    return clientRankBars((data && data.charts && data.charts.porCliente) || [], prog || undefined);
  }, [data, kpis]);
  const weekMax = Math.max(1, ...weeks.map((w) => w.value), 1);

  const funnel = (data && data.funnel) || [];
  const funnelMax = Math.max(
    1,
    ...funnel.map((s) => (s && s.value != null ? Number(s.value) : 0)).filter((n) => n >= 0)
  );

  const tableRows = ((data && data.table && data.table.rows) || []).filter((row) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return Object.values(row).some((v) => String(v || '').toLowerCase().includes(q));
  });

  const primaryCards = PRIMARY_KPIS.map((label) => {
    const k = kpis[label];
    return {
      label,
      value: k && k.value != null && k.value !== '' ? k.value : null,
      hint: (k && k.hint) || undefined
    };
  });

  return (
    <div
      className={
        'analytics-module analytics-reuniones-page' + (loading ? ' is-loading-period' : '')
      }
    >
      <PageHeader
        eyebrow="Módulo · Reuniones"
        title="Reuniones"
        subtitle={
          'Cuántas y con qué cliente (Kommo o GIC)' + (email ? ` · ${email}` : '')
        }
        actions={
          <ModuleFilters
            filters={filters}
            disabled={false}
            busy={loading}
            onRefresh={runSearch}
            onCancel={cancelSearch}
            executives={[]}
            projects={[]}
            channels={[]}
            statuses={catalogs.statuses || []}
            showExecutive={false}
            showChannel={false}
            showProject={false}
            showStatus={true}
          />
        }
      />

      {!hasSearched && !loading ? (
        <p className="analytics-empty">
          Elegí el período y pulsá <strong>Buscar</strong>. No se consulta hasta entonces.
        </p>
      ) : null}

      {loading ? (
        <div className="analytics-leads-loading" role="status" aria-live="polite">
          <span className="analytics-leads-loading-dot" aria-hidden />
          {data ? 'Buscando de nuevo…' : 'Buscando reuniones…'}
        </div>
      ) : null}

      <SourceBanner mode={(data && data.sourceStatus) || (loading ? 'ok' : 'unconnected')}>
        {(data && data.message) ||
          'Reuniones por cliente. Kommo si hay vínculo; si no, Cliente GIC igual cuenta.'}
      </SourceBanner>
      <ErrorState
        message={error}
        onRetry={runSearch}
      />

      {data ? (
        <>
          <section className={'reu-reading is-' + (reading.tone || 'info')} aria-live="polite">
            <p className="reu-reading-kicker">Lectura del período</p>
            <h2 className="reu-reading-title">{reading.title}</h2>
            <p className="reu-reading-body">{reading.body}</p>
          </section>

          <section className="analytics-section">
            <SectionHeader
              title="Lo esencial"
              note="Con quién fue la reunión: contacto Kommo, cliente GIC sin Kommo, o sin cliente."
            />
            <div className="reu-kpi-strip">
              {primaryCards.map((c) => (
                <article key={c.label} className="reu-kpi">
                  <p className="reu-kpi-label">{c.label}</p>
                  <p className="reu-kpi-value">
                    {c.value == null ? 'Sin datos' : formatAnalyticsNumber(c.value)}
                  </p>
                  {c.hint ? <p className="reu-kpi-hint">{c.hint}</p> : null}
                </article>
              ))}
            </div>
          </section>

          <div className="reu-split">
            <section className="analytics-section reu-split-main">
              <SectionHeader
                title="Embudo"
                note="De agendadas a cliente identificado, Kommo, minuta y resultado comercial."
              />
              <div className="analytics-panel reu-funnel">
                {funnel.length ? (
                  funnel.map((step) => {
                    const missing = step.value == null || step.value === '';
                    const v = Number(step.value);
                    const pct = !missing && Number.isFinite(v) ? Math.round((v / funnelMax) * 100) : 0;
                    return (
                      <div key={step.label} className="reu-funnel-row">
                        <span className="reu-funnel-label">{step.label}</span>
                        <div className="reu-funnel-track">
                          {!missing ? (
                            <div className="reu-funnel-fill" style={{ width: pct + '%' }} />
                          ) : (
                            <span className="reu-funnel-nodata">Sin datos</span>
                          )}
                        </div>
                        <span className="reu-funnel-value num">
                          {!missing ? formatAnalyticsNumber(v) : '—'}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="analytics-empty">Sin embudo.</p>
                )}
              </div>
            </section>

            <section className="analytics-section reu-split-side">
              <SectionHeader
                title="Tipo de vínculo"
                note="Kommo vs cliente GIC sin Kommo vs sin cliente."
              />
              <div className="analytics-panel">
                <SimplePieChart rows={(data.charts && data.charts.porVinculo) || []} />
              </div>
            </section>
          </div>

          <div className="reu-split">
            <section className="analytics-section reu-split-main">
              <SectionHeader
                title="Ritmo semanal"
                note="Agendadas agrupadas por semana."
              />
              <div className="analytics-panel reu-weeks">
                {weeks.length ? (
                  <div className="reu-week-cols" role="img" aria-label="Reuniones por semana">
                    {weeks.map((w) => (
                      <div key={w.weekStart} className="reu-week-col" title={w.weekStart}>
                        <div className="reu-week-bar-wrap">
                          <div
                            className="reu-week-bar"
                            style={{ height: Math.max(8, Math.round((w.value / weekMax) * 100)) + '%' }}
                          />
                        </div>
                        <span className="reu-week-val num">{formatAnalyticsNumber(w.value)}</span>
                        <span className="reu-week-lbl">{w.label}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="analytics-empty">Sin fechas para agrupar.</p>
                )}
              </div>
            </section>

            <section className="analytics-section reu-split-side">
              <SectionHeader
                title="Con quién"
                note="Ranking por contacto Kommo o cliente GIC. % sobre agendadas del período."
              />
              <div className="analytics-panel reu-rank">
                {clientRank.length ? (
                  <table className="reu-rank-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Cliente / contacto</th>
                        <th className="num">Reuniones</th>
                        <th className="num">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientRank.slice(0, 15).map((r) => (
                        <tr key={r.label + (r.kommoContactId || r.clienteId || '')}>
                          <td>{r.rank}</td>
                          <td>
                            <div className="reu-rank-name">{r.label}</div>
                            {r.vinculoLabel ? (
                              <div className="reu-kpi-hint">{r.vinculoLabel}</div>
                            ) : null}
                            <div className="reu-rank-share-track">
                              <div
                                className="reu-rank-share-fill"
                                style={{ width: Math.min(100, r.share) + '%' }}
                              />
                            </div>
                          </td>
                          <td className="num">{formatAnalyticsNumber(r.value)}</td>
                          <td className="num">{formatAnalyticsNumber(r.share)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="analytics-empty">Sin clientes en el período.</p>
                )}
              </div>
            </section>
          </div>

          {(data.alerts || []).length ? (
            <section className="analytics-section">
              <SectionHeader title="Alertas" />
              <div className="analytics-panel">
                <AlertList
                  items={(data.alerts || []).map((a) => ({
                    id: a.id,
                    severity: a.severity || a.tone || 'warn',
                    title: a.title,
                    detail: a.detail || a.message
                  }))}
                />
              </div>
            </section>
          ) : null}

          <section className="analytics-section">
            <SectionHeader
              title="Detalle"
              note="Cada fila = una reunión. Buscá por nombre, Cliente ID o Kommo."
            />
            <SimpleTable
              columns={(data.table && data.table.columns) || []}
              rows={tableRows}
              search={search}
              onSearchChange={setSearch}
              emptyText="Sin filas en el período."
            />
          </section>
        </>
      ) : null}
    </div>
  );
}
